import { Component, Input, Output, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';

import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { CustomerService, FeeInfo } from '../../../../../../../../../services/customer/customer.service';
import { propExists } from '../../../../../../../../../utils';
import { OrganizationConfigService } from '../../../../../../../../../services/organization-config/organization-config.service';
import { CustomerMembership, CustomerMembershipCustomer, MembershipPerCustomerAddOn } from '../../../../../../../../../models/membership';
import { initPayment } from '../../../../../../modals/utils';
import { debounceTime } from 'rxjs/operators';
import { MembershipPurchaseType } from '../../../../../../../../../models/membership-enums';

@Component({
  selector: 'app-edit-customer-membership-add-ons-immediately',
  templateUrl: './edit-customer-membership-add-ons-immediately.component.html',
  styleUrls: ['./edit-customer-membership-add-ons-immediately.component.css']
})
export class EditCustomerMembershipAddOnsImmediatelyComponent implements OnInit, OnDestroy {

  @Input() membership: CustomerMembership;
  @Input() savedCallback: (successMessage: string) => undefined;
  @Input() saveClickedObservable: Observable<void>;
  @Output() loadingEvent = new EventEmitter<boolean>();
  @Output() serverErrorEvent = new EventEmitter<number>();

  customerAddOns: {[key: string]: MembershipPerCustomerAddOn[]} = {};

  public initialAddOnFeeTotal = 0;

  public selectedAddOnsChangedSubject = new Subject<null>();
  public selectedAddOnChangeLoading = false;
  public selectedAddOnChangeFeeRequestCount = 0;
  public selectedAddOnChangeFee: FeeInfo = {
    fee_amount: '0.00',
    fee_is_less_than_minimum: false
  };

  public errorMessage = '';

  public readonly MembershipPurchaseType = MembershipPurchaseType;

  private saveClickedSubscription: Subscription;

  constructor(
    public orgConfig: OrganizationConfigService,
    private activeModal: NgbActiveModal,
    private customerService: CustomerService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.saveClickedSubscription = this.saveClickedObservable.subscribe(() => {
      this.handleSave();
    });

    this.initialAddOnFeeTotal = 0;

    // Create a deep copy of the customer add-ons, to avoid editing the
    // membership directly (this would cause the membership to be updated in
    // the list before it is changed on the server).
    for (const customer of this.membership.all_customers) {
      const customerIdStr = customer.id.toString();
      this.customerAddOns[customerIdStr] = [];

      if (propExists(this.membership.customer_add_ons[customerIdStr])) {
        for (const addOn of this.membership.customer_add_ons[customerIdStr]) {
          this.customerAddOns[customerIdStr].push(addOn);
          this.initialAddOnFeeTotal += parseFloat(addOn.fee);
        }
      }
    }
    for (const customer of this.membership.unlinked_customers) {
      const customerIdStr = 'u' + customer.id.toString();
      this.customerAddOns[customerIdStr] = [];

      if (propExists(this.membership.customer_add_ons[customerIdStr])) {
        for (const addOn of this.membership.customer_add_ons[customerIdStr]) {
          this.customerAddOns[customerIdStr].push(addOn);
          this.initialAddOnFeeTotal += parseFloat(addOn.fee);
        }
      }
    }

    // Handle changes to the selected add-ons by reloading the change fee.
    this.selectedAddOnsChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadChangeFee();
    });
  }

  ngOnDestroy() {
    this.saveClickedSubscription.unsubscribe();
  }

  public addOnIsSelectedForCustomer(customer: CustomerMembershipCustomer,
                                    addOn: MembershipPerCustomerAddOn) {
    let thisCustomerAddOns = this.customerAddOns[customer.id.toString()];

    if (!propExists(thisCustomerAddOns)) {
      this.customerAddOns[customer.id.toString()] = [];
      thisCustomerAddOns = this.customerAddOns[customer.id.toString()];
    }

    for (const thisAddOn of thisCustomerAddOns) {
      if (thisAddOn.id === addOn.id) {
        return true;
      }
    }

    return false;
  }

  public toggleAddOnForCustomer(customer: CustomerMembershipCustomer,
                                addOn: MembershipPerCustomerAddOn) {
    // Set loading true here so the load indicator displays immediately, rather
    // than after the debounce time
    this.selectedAddOnChangeLoading = true;
    this.selectedAddOnsChangedSubject.next();

    const thisCustomerAddOns = this.customerAddOns[customer.id.toString()];

    for (let i = 0; i < thisCustomerAddOns.length; i++) {
      const thisAddOn = thisCustomerAddOns[i];

      // If the add-on is found in the list, remove it and return.
      if (thisAddOn.id === addOn.id) {
        thisCustomerAddOns.splice(i, 1);
        return;
      }
    }

    // If we get to this line, then the add on was not found in the list, so
    // add it.
    thisCustomerAddOns.push(addOn);
  }

  public getNewFeeTotal() {
    let newFeeTotal = 0;

    for (const customer of this.membership.all_customers) {
      const customerIdStr = customer.id.toString();
      if (propExists(this.customerAddOns[customerIdStr])) {
        for (const addOn of this.customerAddOns[customerIdStr]) {
          newFeeTotal += parseFloat(addOn.fee);
        }
      }
    }
    for (const customer of this.membership.unlinked_customers) {
      const customerIdStr = 'u' + customer.id.toString();
      if (propExists(this.customerAddOns[customerIdStr])) {
        for (const addOn of this.customerAddOns[customerIdStr]) {
          newFeeTotal += parseFloat(addOn.fee);
        }
      }
    }

    return newFeeTotal;
  }

  public mustDisplayMinimumChargeWarning() {
    const feeAmount = parseFloat(this.selectedAddOnChangeFee.fee_amount);

    return (
      (feeAmount > 0) &&
      this.selectedAddOnChangeFee.fee_is_less_than_minimum
    );
  }

  private handleSave() {
    this.loadingEvent.emit(true);

    this.customerService
      .editMembershipCustomerAddOnsInvoice(this.membership.id, this.customerAddOns)
      .then((invoiceIdStr) => {
        if (!propExists(invoiceIdStr) || (invoiceIdStr === '')) {
          this.handleSuccess();
        } else {
          initPayment(
            this.modalService,
            parseInt(invoiceIdStr, 10),
            this.membership.payment_processor_type,
            this.membership.owner_id,
            'Capitan membership #' + this.membership.id.toString() + ' add-on change fee #' + invoiceIdStr,
            location.href,
            location.href,
            () => { this.handleSuccess(); }
          );
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to edit this membership\'s add-ons.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverErrorEvent.emit(reason.status);
        }
      })
      .finally(() => { this.loadingEvent.emit(false); });
  }

  private handleSuccess() {
      this.savedCallback('Membership add-ons edited successfully.');
      this.activeModal.close();
  }

  private loadChangeFee() {
    this.selectedAddOnChangeLoading = true;

    this.selectedAddOnChangeFeeRequestCount++;
    const thisRequestCount = this.selectedAddOnChangeFeeRequestCount;

    this.customerService
      .checkEditMembershipCustomerAddOnsInvoice(
        this.membership.id,
        this.customerAddOns
      )
      .then(feeInfo => {
        if (thisRequestCount === this.selectedAddOnChangeFeeRequestCount) {
          this.selectedAddOnChangeFee = feeInfo
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to edit this membership\'s add-ons.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverErrorEvent.emit(reason.status);
        }
      })
      .finally(() => {
        if (thisRequestCount === this.selectedAddOnChangeFeeRequestCount) {
          this.selectedAddOnChangeLoading = false;
        }
      });
  }

}
